import { Redirect, usePathname, type UnknownOutputParams } from 'expo-router'
import type { PropsWithChildren, ReactNode } from 'react'
import { Platform } from 'react-native'

import { env } from '@/src/config/env'
import { useLoginDetails } from '@/src/features/auth/services/useLoginDetails'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import { getSearchParams } from '@/src/general/utils/getSearchParams'
import type { AUTH_SOURCE } from '@/src/general/utils/gtm/constants/AuthSources.enum'
import { useRoutes } from '@/src/general/utils/routes'

import { useMeQuery } from '../../../services/useMeQuery'
import { getConnectDetails } from '../../../utils/connectDetails'
import { useBfCacheCheck } from './useBfCache'

type TProtectedProps = {
  authSource?: AUTH_SOURCE
  searchParams: UnknownOutputParams
}

export const Protected = ({ children, authSource, searchParams }: PropsWithChildren<TProtectedProps>): ReactNode => {
  const { screen: _screen, params: _params, ...restParams } = searchParams || {}
  const pathname = usePathname()
  const { routes } = useRoutes()

  const { data: me } = useMeQuery()
  const result = useLoginDetails(searchParams)
  const { isLoading, initialized } = result

  useBfCacheCheck()

  if (me) return children

  if (getIsLoggedIn() || isLoading) return null

  const { search } = getSearchParams(restParams)
  const redirectTo = getConnectDetails().redirectUrl ?? `${pathname}${search}`

  if (!initialized) {
    return <Redirect href={routes.authComplete({ redirectTo, authSource })} />
  }

  if (env.EXPO_PUBLIC_LEGACY_COMPATIBILITY && Platform.OS === 'web')
    return <Redirect href={routes.connect(redirectTo)} />

  return <Redirect href={routes.authConnect({ redirectTo, authSource })} />
}
